import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNotificationApi } from "@/api/notifications";
import { notificationQueryKeys } from "./notification-query";

type UseNotificationListOptions = {
  enabled?: boolean;
  pageSize?: number;
  unreadOnly: boolean;
};

export function useNotificationList({ enabled = true, pageSize = 20, unreadOnly }: UseNotificationListOptions) {
  const api = useNotificationApi();
  const query = useInfiniteQuery({
    queryKey: notificationQueryKeys.list(unreadOnly),
    queryFn: ({ pageParam, signal }) => api.getList({ beforeCursor: pageParam, limit: pageSize, unreadOnly }, signal),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (page) => (page.hasMore && page.nextCursor ? page.nextCursor : undefined),
    enabled,
  });

  const notifications = useMemo(() => {
    const known = new Set<number>();
    return (
      query.data?.pages
        .flatMap((page) => page.items)
        .filter((item) => {
          if (known.has(item.cursor)) {
            return false;
          }
          known.add(item.cursor);
          return true;
        }) ?? []
    );
  }, [query.data?.pages]);

  const newestCursor = notifications.reduce((cursor, notification) => Math.max(cursor, notification.cursor), 0);

  return { newestCursor, notifications, query };
}
